import type { Theme } from "@/components/ThemeProvider/ThemeProvider";

export const cppTheme = {
  color: {
    background: {
      accent: {
        primary: {
          rest: "var(--cpp-color-background-accent-primary-rest)",
          hover: "var(--cpp-color-background-accent-primary-hover)",
          active: "var(--cpp-color-background-accent-primary-active)",
          focus: "var(--cpp-color-background-accent-primary-focus)",
        },
        secondary: {
          rest: "var(--cpp-color-background-accent-secondary-rest)",
          hover: "var(--cpp-color-background-accent-secondary-hover)",
          active: "var(--cpp-color-background-accent-secondary-active)",
          focus: "var(--cpp-color-background-accent-secondary-focus)",
        },
        tertiary: {
          rest: "var(--cpp-color-background-accent-tertiary-rest)",
          hover: "var(--cpp-color-background-accent-tertiary-hover)",
          active: "var(--cpp-color-background-accent-tertiary-active)",
          focus: "var(--cpp-color-background-accent-tertiary-focus)",
        },
        inverted: {
          rest: "var(--cpp-color-background-accent-inverted-rest)",
          hover: "var(--cpp-color-background-accent-inverted-hover)",
          active: "var(--cpp-color-background-accent-inverted-active)",
          focus: "var(--cpp-color-background-accent-inverted-focus)",
        },
        disabled: { rest: "var(--cpp-color-background-accent-disabled-rest)" },
      },
      surface: {
        primary: "var(--cpp-color-background-surface-primary)",
        secondary: "var(--cpp-color-background-surface-secondary)",
        tertiary: "var(--cpp-color-background-surface-tertiary)",
        neutral: "var(--cpp-color-background-surface-neutral)",
        contrastInverted: "var(--cpp-color-background-surface-contrast-inverted)",
        disabled: "var(--cpp-color-background-surface-disabled)",
      },
      brand: {
        primary: "var(--cpp-color-background-brand-primary)",
        secondary: "var(--cpp-color-background-brand-secondary)",
        tertiary: "var(--cpp-color-background-brand-tertiary)",
      },
      success: {
        primary: "var(--cpp-color-background-success-primary)",
        secondary: "var(--cpp-color-background-success-secondary)",
        tertiary: "var(--cpp-color-background-success-tertiary)",
      },
      danger: {
        primary: "var(--cpp-color-background-danger-primary)",
        secondary: "var(--cpp-color-background-danger-secondary)",
        tertiary: "var(--cpp-color-background-danger-tertiary)",
      },
      information: {
        primary: "var(--cpp-color-background-information-primary)",
        secondary: "var(--cpp-color-background-information-secondary)",
        tertiary: "var(--cpp-color-background-information-tertiary)",
      },
      warning: {
        primary: "var(--cpp-color-background-warning-primary)",
        secondary: "var(--cpp-color-background-warning-secondary)",
        tertiary: "var(--cpp-color-background-warning-tertiary)",
      },
      interaction: {
        hover: "var(--cpp-color-background-interaction-hover)",
        active: "var(--cpp-color-background-interaction-active)",
        focus: "var(--cpp-color-background-interaction-focus)",
      },
    },
    foreground: {
      text: {
        primary: "var(--cpp-color-foreground-text-primary)",
        secondary: "var(--cpp-color-foreground-text-secondary)",
        tertiary: "var(--cpp-color-foreground-text-tertiary)",
        contrast: "var(--cpp-color-foreground-text-contrast)",
        inverted: "var(--cpp-color-foreground-text-inverted)",
        accent: "var(--cpp-color-foreground-text-accent)",
        success: "var(--cpp-color-foreground-text-success)",
        danger: "var(--cpp-color-foreground-text-danger)",
        warning: "var(--cpp-color-foreground-text-warning)",
        information: "var(--cpp-color-foreground-text-information)",
      },
      icon: {
        primary: "var(--cpp-color-foreground-icon-primary)",
        secondary: "var(--cpp-color-foreground-icon-secondary)",
        tertiary: "var(--cpp-color-foreground-icon-tertiary)",
        inverted: "var(--cpp-color-foreground-icon-inverted)",
      },
    },
    border: {
      layout: {
        subtle: "var(--cpp-color-border-layout-subtle)",
        bold: "var(--cpp-color-border-layout-bold)",
        custom: "var(--cpp-color-border-layout-custom)",
      },
      input: {
        rest: "var(--cpp-color-border-input-rest)",
        hover: "var(--cpp-color-border-input-hover)",
        active: "var(--cpp-color-border-input-active)",
        focus: "var(--cpp-color-border-input-focus)",
      },
    },
  },
  opacity: {
    disabled: "var(--cpp-opacity-disabled)",
    loading: "var(--cpp-opacity-loading)",
    overlay: "var(--cpp-opacity-overlay)",
  },
  shadow: {
    xs: "var(--cpp-shadow-xs)",
    s: "var(--cpp-shadow-s)",
    m: "var(--cpp-shadow-m)",
    l: "var(--cpp-shadow-l)",
    xl: "var(--cpp-shadow-xl)",
    inner: "var(--cpp-shadow-inner)",
  },
  font: {
    family: { body: "var(--cpp-font-family-body)", title: "var(--cpp-font-family-title)" },
    weight: {
      extraLight: "var(--cpp-font-weight-extra-light)",
      light: "var(--cpp-font-weight-light)",
      normal: "var(--cpp-font-weight-normal)",
      medium: "var(--cpp-font-weight-medium)",
      semibold: "var(--cpp-font-weight-semibold)",
      bold: "var(--cpp-font-weight-bold)",
    },
    size: {
      xxs: "var(--cpp-font-size-xxs)",
      xs: "var(--cpp-font-size-xs)",
      s: "var(--cpp-font-size-s)",
      m: "var(--cpp-font-size-m)",
      l: "var(--cpp-font-size-l)",
      xl: "var(--cpp-font-size-xl)",
      xxl: "var(--cpp-font-size-xxl)",
    },
    letterSpacing: {
      negative: "var(--cpp-font-letter-spacing-negative)",
      neutral: "var(--cpp-font-letter-spacing-neutral)",
      positive: "var(--cpp-font-letter-spacing-positive)",
    },
    lineHeight: {
      xxs: "var(--cpp-font-line-height-xxs)",
      xs: "var(--cpp-font-line-height-xs)",
      s: "var(--cpp-font-line-height-s)",
      m: "var(--cpp-font-line-height-m)",
      l: "var(--cpp-font-line-height-l)",
      xl: "var(--cpp-font-line-height-xl)",
      xxl: "var(--cpp-font-line-height-xxl)",
    },
  },
  typography: {
    title: {
      s: "var(--cpp-typography-title-s)",
      m: "var(--cpp-typography-title-m)",
      l: "var(--cpp-typography-title-l)",
    },
    body: {
      bolder: {
        s: "var(--cpp-typography-body-bolder-s)",
        m: "var(--cpp-typography-body-bolder-m)",
        l: "var(--cpp-typography-body-bolder-l)",
      },
      regular: {
        s: "var(--cpp-typography-body-regular-s)",
        m: "var(--cpp-typography-body-regular-m)",
        l: "var(--cpp-typography-body-regular-l)",
      },
    },
    caption: {
      s: "var(--cpp-typography-caption-s)",
      m: "var(--cpp-typography-caption-m)",
      l: "var(--cpp-typography-caption-l)",
    },
  },
  spacing: {
    none: "var(--cpp-spacing-none)",
    xxs: "var(--cpp-spacing-xxs)",
    xs: "var(--cpp-spacing-xs)",
    s: "var(--cpp-spacing-s)",
    m: "var(--cpp-spacing-m)",
    l: "var(--cpp-spacing-l)",
    xl: "var(--cpp-spacing-xl)",
    xxl: "var(--cpp-spacing-xxl)",
  },
  size: {
    xs: "var(--cpp-size-xs)",
    s: "var(--cpp-size-s)",
    m: "var(--cpp-size-m)",
    l: "var(--cpp-size-l)",
    xl: "var(--cpp-size-xl)",
  },
  radii: {
    circular: "var(--cpp-radii-circular)",
    none: "var(--cpp-radii-none)",
    xs: "var(--cpp-radii-xs)",
    s: "var(--cpp-radii-s)",
    m: "var(--cpp-radii-m)",
    l: "var(--cpp-radii-l)",
    xl: "var(--cpp-radii-xl)",
  },
};

export const getThemeTokenValue = function (token: string | null | Theme | undefined) {
  if (typeof token === "string") {
    return getComputedStyle(document.body).getPropertyValue(token.replace("var(", "").replace(")", ""));
  }
  return "";
};
